import React, { useState } from 'react';
import { waitlistAPI } from '../services/api';

/**
 * Waitlist capture for the pre-launch period.
 *
 * Ariv holds bookings shut until 50 hosts have signed up, so guests cannot
 * usefully create an account yet. Rather than letting that demand walk away,
 * this takes an email and remembers which side of the marketplace the person
 * is waiting for, then hands them a referral code so they can bring others
 * while they wait.
 *
 * Two things it records that the old email-only form did not:
 *
 *   role        which side they want. Without it the list is a pile of email
 *               addresses and there is no way to tell a waiting host from a
 *               waiting guest when deciding who to telephone first.
 *   acquisition where they came from, carried by withAcquisition in
 *               services/api.js. A join through a partner's tracked link
 *               would otherwise be invisible, and partners paid per
 *               introduction would go unpaid for it.
 *
 * `defaultRole` preselects the right answer for the page it sits on, so the
 * host landing page offers hosting first and the homepage offers staying.
 */
export default function WaitlistSignup({
  defaultRole = 'guest',
  heading = 'Join the waitlist',
  blurb = 'We open to guests once fifty hosts are live. Leave your email and we will tell you the moment that happens.',
  compact = false,
}) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState(defaultRole);
  const [sending, setSending] = useState(false);
  const [joined, setJoined] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes('@')) {
      setError('Please enter an email address we can reach you on.');
      return;
    }
    setError(null);
    setSending(true);
    try {
      // The referral code of whoever sent them, if they arrived through a
      // share link. Read here rather than stored, so nothing is kept on the
      // visitor's device before they choose to join.
      const referredBy = new URLSearchParams(window.location.search).get('ref');
      const data = await waitlistAPI.join({ email: trimmed, role, referredBy });
      setJoined(data);
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          'We could not add you just then. Please try again in a moment.'
      );
    } finally {
      setSending(false);
    }
  };

  if (joined) {
    return (
      <div className="rounded-xl border border-[#1F3A68]/20 bg-[#F4F2E9] p-6">
        <h3 className="text-xl font-semibold text-[#1F3A68]">
          {joined.already_joined ? 'You are already on the list' : 'You are on the list'}
        </h3>
        <p className="mt-2 text-gray-700">
          We will email you as soon as we open. In the meantime, anyone who joins through
          your link counts as your introduction.
        </p>
        {joined.shareUrl && (
          <div className="mt-4">
            <p className="text-sm font-semibold uppercase tracking-wide text-[#B04A1E]">
              Your link
            </p>
            <p className="mt-1 break-all font-medium text-[#1F3A68]">{joined.shareUrl}</p>
            <button
              type="button"
              onClick={() => navigator.clipboard?.writeText(joined.shareUrl)}
              className="mt-3 rounded-lg bg-[#1F3A68] px-4 py-2 font-semibold text-white"
            >
              Copy link
            </button>
          </div>
        )}
        {typeof joined.referralCount === 'number' && joined.referralCount > 0 && (
          <p className="mt-4 text-sm text-gray-600">
            {joined.referralCount} {joined.referralCount === 1 ? 'person has' : 'people have'}{' '}
            joined through you so far.
          </p>
        )}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-xl border border-[#1F3A68]/20 bg-[#F4F2E9] p-6"
    >
      <h3 className="text-xl font-semibold text-[#1F3A68]">{heading}</h3>
      {!compact && <p className="mt-2 text-gray-700">{blurb}</p>}

      <label htmlFor="waitlist-role" className="mt-5 block text-sm font-semibold text-[#1F3A68]">
        What are you waiting for?
      </label>
      <select
        id="waitlist-role"
        value={role}
        onChange={(e) => setRole(e.target.value)}
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900"
      >
        <option value="guest">Somewhere to stay</option>
        <option value="host">Listing a property of my own</option>
        <option value="service_provider">Offering cleaning or a trade</option>
        <option value="landlord">Letting a property to an operator</option>
        <option value="driver">Driving</option>
        <option value="restaurant_partner">Taking food orders</option>
        <option value="delivery_rider">Delivering</option>
      </select>

      <label htmlFor="waitlist-email" className="mt-4 block text-sm font-semibold text-[#1F3A68]">
        Email
      </label>
      <input
        id="waitlist-email"
        type="email"
        autoComplete="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="you@example.com"
        className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2.5 text-gray-900"
      />

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <button
        type="submit"
        disabled={sending}
        className="mt-5 w-full rounded-lg bg-[#1F3A68] px-4 py-3 font-semibold text-white disabled:opacity-60"
      >
        {sending ? 'Adding you…' : 'Join the waitlist'}
      </button>

      <p className="mt-3 text-xs leading-relaxed text-gray-600">
        We will only use your email to tell you when Ariv opens and nothing else. You can
        ask us to remove you at any time.
      </p>
    </form>
  );
}
